import { useContext, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';

import Table from 'components/common/Table/Table';
import TableBody from 'components/common/Table/TableBody/TableBody';
import TableHead from 'components/common/Table/TableHead/TableHead';
import TableHeadCell from 'components/common/Table/TableHeadCell/TableHeadCell';
import { RootStoreContext } from 'index';
import Pagination from 'components/common/Table/Pagination/Pagination';
import SideBar from 'components/common/SideBar/SideBar';
import { hasPermission } from 'components/permissions/permissions.utils';
import { PermissionName } from 'components/permissions/permissions.enum';

import styles from './UsersTable.module.scss';
import { UserPaginationItemDto } from '../../users.dto';
import UsersTableRow from './UsersTableRow/UsersTableRow';
import { MAX_USERS_PER_PAGE, USERS_TABLE_COLUMNS, USERS_TABLE_COLUMNS_AMOUNT } from '../../users.constants';
import UsersTableSkeletonRow from './UsersTableSkeletonRow/UsersTableSkeletonRow';
import { SortDirection, UserProfileOpenMode, UsersTableSortField } from '../../users.enums';
import UserProfileForm from '../UserProfileForm/UserProfileForm';
import UsersDeleteForm from '../UserDeleteForm/UserDeleteForm';

function UsersTable() {
  const { usersStore, authStore } = useContext(RootStoreContext);
  const { users, isLoading, filters, totalUsers } = usersStore;
  const [isUserProfileViewModeOpen, setIsUserProfileViewModeOpen] = useState<boolean>(false);
  const [isUserProfileEditModeOpen, setIsUserProfileEditModeOpen] = useState<boolean>(false);
  const [isUserProfileDeleteModeOpen, setIsUserProfileDeleteModeOpen] = useState<boolean>(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const canUserView = hasPermission(authStore.account!.permissions, [PermissionName.UserView]);

  const skeletonRows = Array.from(Array(USERS_TABLE_COLUMNS_AMOUNT).keys());

  const getInitialData = async () => {
    await usersStore.getAllUsers();
  };

  useEffect(() => {
    getInitialData();
  }, [filters]);

  const updateTableSort = (sortField?: UsersTableSortField) => () => {
    if (!sortField) {
      return;
    }

    if (filters.sortField === sortField) {
      usersStore.updateFilters({
        ...filters,
        sortDirection: filters.sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc,
      });

      return;
    }
  };

  const handlePageCountChange = (pageIndex: number) => {
    usersStore.updateFilters({ ...filters, pageIndex: pageIndex });
  };

  const openUserProfileViewMode = (userId: number) => {
    if (!canUserView) {
      return;
    }

    setCurrentUserId(userId);
    setIsUserProfileViewModeOpen(true);
  };

  const handleUserProfileViewModeClose = () => {
    setIsUserProfileViewModeOpen(false);
  };

  const openUserProfileEditMode = (userId: number) => {
    setCurrentUserId(userId);
    setIsUserProfileEditModeOpen(true);
  };

  const handleUserProfileEditModeClose = () => {
    setIsUserProfileEditModeOpen(false);
  };

  const openUserProfileDelete = (userId: number) => {
    setCurrentUserId(userId);
    setIsUserProfileDeleteModeOpen(true);
  };

  const handleUserProfileDeleteClose = () => {
    setIsUserProfileDeleteModeOpen(false);
  };

  const renderTableContent = () => {
    return !users.length ? (
      <div className={styles.emptyLine}>No Users</div>
    ) : (
      <>
        {users.map((user: UserPaginationItemDto) => (
          <UsersTableRow
            key={user.id}
            user={user}
            onClick={() => openUserProfileViewMode(user.id)}
            onEditClick={(event: any) => {
              event.stopPropagation();
              openUserProfileEditMode(user.id);
            }}
            onDeleteClick={(event: any) => {
              event.stopPropagation();
              openUserProfileDelete(user.id);
            }}
          />
        ))}
      </>
    );
  };

  const renderTableHeader = () => {
    return (
      <TableHead className={styles.tableRowTemplate}>
        {USERS_TABLE_COLUMNS.map((column, index) => (
          <TableHeadCell
            key={index}
            isExist
            isSorted={!!column.sortField}
            currentSort={filters.sortField === column.sortField}
            isSortedDesc={filters.sortDirection === SortDirection.Asc}
            onClick={updateTableSort(column.sortField)}
          >
            <div className={styles.tableHeaderCell}>{column.title}</div>
          </TableHeadCell>
        ))}
      </TableHead>
    );
  };

  return (
    <div>
      <Table className={styles.usersTable}>
        {renderTableHeader()}
        <TableBody>
          {isLoading ? (
            <>
              {skeletonRows.map((skeletonRow: number) => (
                <UsersTableSkeletonRow key={skeletonRow} />
              ))}
            </>
          ) : (
            renderTableContent()
          )}
        </TableBody>
      </Table>
      {!isLoading && !!users.length && (
        <Pagination
          pageIndex={filters.pageIndex}
          itemsPerPage={MAX_USERS_PER_PAGE}
          itemsTotal={totalUsers}
          pagesCount={Math.ceil(totalUsers / MAX_USERS_PER_PAGE)}
          onPageIndexChange={handlePageCountChange}
        />
      )}
      <SideBar title="User View" isOpen={isUserProfileViewModeOpen} onClose={handleUserProfileViewModeClose}>
        <UserProfileForm
          userId={currentUserId!}
          openMode={UserProfileOpenMode.View}
          onClose={handleUserProfileViewModeClose}
        />
      </SideBar>
      <SideBar title="User Edit" isOpen={isUserProfileEditModeOpen} onClose={handleUserProfileEditModeClose}>
        <UserProfileForm
          userId={currentUserId!}
          openMode={UserProfileOpenMode.Edit}
          onClose={handleUserProfileEditModeClose}
        />
      </SideBar>
      <SideBar title="Delete User" isOpen={isUserProfileDeleteModeOpen} onClose={handleUserProfileDeleteClose}>
        <UsersDeleteForm userId={currentUserId!} onClose={handleUserProfileDeleteClose} />
      </SideBar>
    </div>
  );
}

export default observer(UsersTable);
